import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, ExternalLink, Eye, EyeOff, AlertCircle } from "lucide-react";
import { fetchAllPosts, deletePost } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/auth";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("it-CH", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
        return iso;
    }
}

export default function AdminBlogList() {
    const [state, setState] = useState({ loading: true, posts: [], error: null });
    const [pendingDelete, setPendingDelete] = useState(null);
    const [delState, setDelState] = useState({ loading: false, error: null });

    async function load() {
        setState((s) => ({ ...s, loading: true, error: null }));
        try {
            const posts = await fetchAllPosts();
            setState({ loading: false, posts, error: null });
        } catch (e) {
            setState({ loading: false, posts: [], error: formatApiErrorDetail(e.response?.data?.detail) || e.message });
        }
    }
    useEffect(() => { load(); }, []);

    async function onConfirmDelete() {
        if (!pendingDelete) return;
        setDelState({ loading: true, error: null });
        try {
            await deletePost(pendingDelete.id);
            setPendingDelete(null);
            setDelState({ loading: false, error: null });
            await load();
        } catch (e) {
            setDelState({ loading: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message });
        }
    }

    return (
        <>
            <div className="flex items-start justify-between mb-10">
                <div>
                    <div className="label-eyebrow text-brand">Blog</div>
                    <h1 className="mt-2 font-heading font-black text-4xl lg:text-5xl tracking-tighter">Articoli</h1>
                    <p className="mt-2 text-ink-muted text-sm">Crea, modifica e pubblica i contenuti del blog 7MED.</p>
                </div>
                <Link to="/admin/blog/new" className="btn-primary" data-testid="admin-new-post">
                    <Plus className="w-4 h-4" /> Nuovo articolo
                </Link>
            </div>

            {state.loading && <div className="p-8 text-center text-ink-muted">Caricamento…</div>}
            {state.error && (
                <div className="p-4 bg-red-50 border border-red-300 text-red-900 flex gap-2 items-start">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div>{state.error}</div>
                </div>
            )}

            {!state.loading && !state.error && state.posts.length === 0 && (
                <div className="border-2 border-dashed border-ink/20 p-16 text-center" data-testid="admin-empty">
                    <h2 className="font-heading font-bold text-2xl">Nessun articolo ancora.</h2>
                    <p className="mt-2 text-ink-muted">Inizia a scrivere il tuo primo post.</p>
                    <Link to="/admin/blog/new" className="btn-primary mt-6"><Plus className="w-4 h-4" /> Nuovo articolo</Link>
                </div>
            )}

            {!state.loading && state.posts.length > 0 && (
                <div className="bg-white border border-ink/10">
                    <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-ink/10 text-[10px] font-mono uppercase tracking-[0.2em] text-ink-muted">
                        <div className="col-span-6">Titolo</div>
                        <div className="col-span-2">Stato</div>
                        <div className="col-span-2">Aggiornato</div>
                        <div className="col-span-2 text-right">Azioni</div>
                    </div>
                    {state.posts.map((p) => (
                        <div key={p.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-ink/5 last:border-b-0 items-center hover:bg-ink/[0.02]" data-testid={`admin-post-row-${p.slug}`}>
                            <div className="col-span-6">
                                <div className="font-bold truncate">{p.title}</div>
                                <div className="text-xs text-ink-muted mt-0.5 truncate">/{p.slug} · {p.category}</div>
                            </div>
                            <div className="col-span-2">
                                {p.status === "published" ? (
                                    <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-emerald-50 text-emerald-700 text-xs font-mono uppercase"><Eye className="w-3 h-3" /> Pubblicato</span>
                                ) : (
                                    <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-amber-50 text-amber-700 text-xs font-mono uppercase"><EyeOff className="w-3 h-3" /> Bozza</span>
                                )}
                            </div>
                            <div className="col-span-2 text-xs text-ink-muted">{formatDate(p.updated_at)}</div>
                            <div className="col-span-2 flex items-center justify-end gap-1">
                                {p.status === "published" && (
                                    <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 text-ink-muted hover:text-brand" title="Vedi pubblico">
                                        <ExternalLink className="w-4 h-4" />
                                    </a>
                                )}
                                <Link to={`/admin/blog/${p.id}`} className="p-2 text-ink-muted hover:text-brand" title="Modifica" data-testid={`admin-edit-${p.slug}`}>
                                    <Pencil className="w-4 h-4" />
                                </Link>
                                <button
                                    onClick={() => { setDelState({ loading: false, error: null }); setPendingDelete(p); }}
                                    className="p-2 text-ink-muted hover:text-brand"
                                    title="Elimina"
                                    data-testid={`admin-delete-${p.slug}`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
                <AlertDialogContent className="rounded-none">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Eliminare questo articolo?</AlertDialogTitle>
                        <AlertDialogDescription>
                            L'articolo "<strong>{pendingDelete?.title}</strong>" verrà cancellato definitivamente dal database. L'operazione non è reversibile.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    {delState.error && <div className="text-sm text-red-600">{delState.error}</div>}
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none">Annulla</AlertDialogCancel>
                        <AlertDialogAction onClick={onConfirmDelete} disabled={delState.loading} className="bg-brand hover:bg-brand-hover rounded-none text-white" data-testid="admin-delete-confirm">
                            {delState.loading ? "Eliminazione..." : "Elimina"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
